import { AffiliatePromotion } from '../../../types/base/affiliate/promotion';
import { ThrottleValues } from '../types';

const defaultLength = 1000 * 60 * 60 * 1; // 1 hr
const fetchDomains = ['onelink.me', 'appsflyer.com', 'impression']; // Domains that should use fetch instead of image or iframe for impression tracking

export function modifyImpressionUrls(
    impressionUrls: AffiliatePromotion['additional_attributes']['impression_urls_meta'],
    advertiserId = ''
) {
    return impressionUrls.map(url => {
        let modifiedUrl = url.url;
        if (fetchDomains.some(domain => modifiedUrl.includes(domain))) {
            const userAgent = navigator.userAgent;
            const isAndroid = /android/i.test(userAgent);

            if (isAndroid) {
                modifiedUrl += `${modifiedUrl.includes('?') ? '&' : '?'}af_ua=${encodeURIComponent(
                    userAgent
                )}&advertiser_id=${encodeURIComponent(advertiserId)}`;
            } else {
                modifiedUrl += `${modifiedUrl.includes('?') ? '&' : '?'}af_ua=${encodeURIComponent(userAgent)}`;
            }
        }
        return {
            ...url,
            url: modifiedUrl,
        };
    });
}

export function fireImpressions(
    impressionUrls: AffiliatePromotion['additional_attributes']['impression_urls_meta'],
    affiliateId: number,
    ttl: number,
    throttle: number,
    throttleClick: number
) {
    storeImpressionUrlsByAffiliate(impressionUrls, affiliateId);
    const impressionPromises = impressionUrls
        .map(url => {
            const impressionLength = getImpressionLength(url);
            return shouldFireImpression('bam_imp_limits', url.url, impressionLength, affiliateId, url.contextOption, {
                ttl,
                throttle,
                throttleClick,
            })
                ? fireImpressionMethod(url.url)
                : null;
        })
        .filter(Boolean);

    Promise.all(impressionPromises).catch(e => {
        // eslint-disable-next-line no-console
        console.error('BAM Impression Error: ', e);
    });
}

function storeImpressionUrlsByAffiliate(
    impressionUrls: AffiliatePromotion['additional_attributes']['impression_urls_meta'],
    affiliateId: number
) {
    const BAM_ASSETS_URL = 'https://b-static.bet-links.com/0.0.2/bam-scripts/assets';

    const urls = impressionUrls.map(u => u.url);
    const params = new URLSearchParams({
        affiliateId: affiliateId.toString(),
        urls: JSON.stringify(urls),
    });

    const iframe = document.createElement('iframe');
    iframe.src = `${BAM_ASSETS_URL}/store.html?${params.toString()}`;
    iframe.sandbox.add('allow-scripts', 'allow-same-origin');
    iframe.style.width = '1px';
    iframe.style.height = '1px';
    iframe.style.position = 'absolute';
    iframe.style.left = '-9999px';
    document.body.appendChild(iframe);

    setTimeout(() => document.body.removeChild(iframe), 5000);
}

export function shouldFireImpression(
    lsKey: string,
    key: string,
    impLength: number,
    affiliateId: number,
    type: string,
    throttleData: {
        ttl: number;
        throttle: number;
        throttleClick: number;
    }
): boolean {
    // Only fire impressions on mobile
    if (window.innerWidth > 576) {
        return false;
    }

    // Check on impression throttling if throttle is less than 1, but greater than 0
    if (!checkThrottle(affiliateId, type, throttleData.ttl, throttleData.throttle, throttleData.throttleClick)) {
        return false;
    }

    try {
        // Get the limits from local storage
        const limits = JSON.parse(localStorage.getItem(lsKey) || '{}') as unknown as Record<string, string>;
        const currentTime = Date.now();
        const impressionLength = impLength || defaultLength;

        const expireTime = limits[key];
        if (!expireTime) {
            limits[key] = (currentTime + (impressionLength || defaultLength)).toString();
            localStorage.setItem(lsKey, JSON.stringify(limits));
            return true;
        }

        if (currentTime > Number(expireTime)) {
            limits[key] = (currentTime + (impressionLength || defaultLength)).toString();
            localStorage.setItem(lsKey, JSON.stringify(limits));
            return true;
        }
    } catch (e) {
        // If we can't parse the limits, just return true
        return false;
    }

    return false;
}

// Separating click/impression logic to make it easier to change in the future
export function getImpressionLength(url: AffiliatePromotion['additional_attributes']['impression_urls_meta'][0]) {
    if (url.contextOption.includes('click')) {
        return defaultLength;
    }

    return defaultLength;
}

function fireImpressionMethod(url: string) {
    // if the url includes any of the fetch domains, use fetch instead of image or iframe
    if (fetchDomains.some(domain => url.includes(domain))) {
        fetch(url, {
            method: 'GET',
            mode: 'no-cors',
        });
    } else {
        // For non-impression URLs, use the iframe
        buildIframe(url);
    }

    return;
}

function buildIframe(url: string) {
    const iframe = document.createElement('iframe');
    iframe.sandbox.add('allow-scripts', 'allow-same-origin');
    iframe.style.width = '1px';
    iframe.style.height = '1px';
    iframe.style.position = 'absolute';
    iframe.style.left = '-9999px';
    iframe.src = url;
    document.body.appendChild(iframe);
}

function checkThrottle(
    affiliateId: number,
    type: string,
    ttl: number,
    throttle: number,
    throttleClick: number
): boolean {
    let throttleKey = `bam_throttle`;
    let throttlePercent = throttle;

    if (type.includes('click')) {
        throttleKey = `bam_throttle_click`;
        throttlePercent = throttleClick;
    }

    // If throttle is 1, just return true
    if (
        (throttleKey === 'bam_throttle' && throttle === 1) ||
        (throttleKey === 'bam_throttle_click' && throttleClick === 1)
    ) {
        return true;
    }

    try {
        const throttleValues = JSON.parse(localStorage.getItem(throttleKey) || '{}') as unknown as Record<
            string,
            ThrottleValues
        >;

        const affKey = affiliateId.toString();
        const currentTime = Date.now();
        let needToRoll = true;

        // If we have a throttle value for this affiliate, check the TTL
        if (throttleValues[affKey]) {
            // check the ttl if we need to roll again
            const lastImpression = Number(throttleValues[affKey].lastImpression);
            if (currentTime - lastImpression < ttl * 1000) {
                needToRoll = false;
            }
        } else {
            throttleValues[affKey] = {
                lastImpression: 0,
                shouldFire: true,
            };
        }

        // If we need roll/check throttle with a random number
        if (needToRoll) {
            throttleValues[affKey].lastImpression = currentTime;
            throttleValues[affKey].shouldFire = Math.random() < throttlePercent;
        }

        // Set value to local storage
        localStorage.setItem(throttleKey, JSON.stringify(throttleValues));

        return throttleValues[affKey].shouldFire;
    } catch (e) {
        // If we can't parse the local storage, just return false
        return false;
    }
}
